import type { QcField, StageRun } from '@nucleus-commerce/api-client/admin';

export type QcAnswerValue = boolean | number | string | null;
export type QcAnswers = Record<string, QcAnswerValue>;

/**
 * Starting answers: the previous submission when there is one (rework edits it), otherwise the
 * backend's prefill, keyed by the checklist field's key (not by its prefill source). A prefilled
 * choice outside the options still lands as text so an "allow other" field keeps it.
 */
export function initialQcAnswers(
  fields: QcField[],
  run: Pick<StageRun, 'prefill' | 'qcSubmissions'>,
): QcAnswers {
  const last = run.qcSubmissions.at(-1);
  const answers: QcAnswers = {};
  for (const field of fields) {
    const previous = last?.answers[field.key];
    if (previous !== undefined) {
      answers[field.key] = previous;
      continue;
    }
    const prefilled = run.prefill[field.key];
    if (prefilled && field.type !== 'file') {
      if (field.type === 'number') {
        const n = Number(prefilled);
        answers[field.key] = Number.isFinite(n) ? n : null;
      } else if (field.type === 'yes_no') {
        answers[field.key] = null;
      } else if (field.type === 'choice' && !field.allowOther) {
        const match = field.options.find((o) => o.toLowerCase() === prefilled.toLowerCase());
        answers[field.key] = match ?? null;
      } else if (field.type === 'choice') {
        answers[field.key] =
          field.options.find((o) => o.toLowerCase() === prefilled.toLowerCase()) ?? prefilled;
      } else {
        answers[field.key] = prefilled;
      }
      continue;
    }
    answers[field.key] = null;
  }
  return answers;
}

const isEmpty = (value: QcAnswerValue | undefined) =>
  value === undefined || value === null || (typeof value === 'string' && value.trim() === '');

/** Keys of required fields left empty; a No is an answer, never a gap. */
export function missingRequired(fields: QcField[], answers: QcAnswers): string[] {
  return fields.filter((f) => f.required && isEmpty(answers[f.key])).map((f) => f.key);
}

/** The submit body: empty answers are sent as null so the backend sees every field. */
export function toQcAnswersBody(fields: QcField[], answers: QcAnswers): QcAnswers {
  return Object.fromEntries(
    fields.map((field) => {
      const value = answers[field.key];
      return [
        field.key,
        isEmpty(value) ? null : typeof value === 'string' ? value.trim() : (value ?? null),
      ];
    }),
  );
}
